import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/shallow';

import {
    GetMainRepresentationByArticleIdLayerLoadMode as LayerLoadMode,
    type PrefabModel
} from 'api/models';
import { useGetMainRepresentationByArticleIdHook } from 'api/queries/articles/articles';
import { GroupType } from 'app/modules/wallforming/components/scenes/objects/articles/types/groupType';
import { isPlacementIndicator } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { FONT_SIZE } from 'app/modules/wallforming/components/scenes/objects/dimensions/buildAtlas';
import {
    layoutDimensionDrawing,
    type DimensionDrawing
} from 'app/modules/wallforming/components/scenes/objects/dimensions/core/dimensionLayout';
import {
    buildDimensionScene,
    getAccessoryArticleIds,
    type DimensionElementTypes
} from 'app/modules/wallforming/components/scenes/objects/dimensions/core/dimensionScene';
import {
    createDisplayMapping,
    createViewFrame,
    type DisplayMapping,
    type ViewFrame
} from 'app/modules/wallforming/components/scenes/objects/dimensions/core/viewFrame';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { Vector3 } from 'three';

export interface DimensionDrawingResult {
    drawing: DimensionDrawing;
    frame: ViewFrame;
    mapping: DisplayMapping;
}

const DIMENSION_UPDATE_INTERVAL_MS = 100;
const REPRESENTATION_STALE_TIME = 24 * 60 * 60 * 1000;

let measureContext: CanvasRenderingContext2D | null = null;
const textWidthCache = new Map<string, number>();

/** Width of a text at font size 1, measured with the same font the text atlas uses. */
function measureText(text: string): number {
    let width = textWidthCache.get(text);
    if (width === undefined) {
        measureContext ??= document.createElement('canvas').getContext('2d');
        if (!measureContext) {
            return text.length * 0.6;
        }
        measureContext.font = `400 ${FONT_SIZE}px Arial`;
        width = measureContext.measureText(text).width / FONT_SIZE;
        textWidthCache.set(text, width);
    }
    return width;
}

type RepresentationQueryResult = UseQueryResult<{ data?: PrefabModel }>;
const combineRepresentations = (results: RepresentationQueryResult[]) => results.map((r) => r.data?.data);

/**
 * Builds the 2D dimension drawing of a wall POI: scene (3D → view) → display mapping → layout.
 * Only accessory articles need their full prefab geometry (graphic + TwoD layers); panels use their COVERAREA colliders.
 */
export const useDimensionDrawing = (
    selectedPoiIndex: number,
    mid: Vector3,
    elementTypes: DimensionElementTypes
): DimensionDrawingResult | null => {
    const {
        workingPlane,
        articleColliders,
        buildingColliders,
        poi,
        articles,
        instanceToGroupMap,
        articleGroups,
        settings
    } = usePlacementStore(
        useShallow((state) => ({
            workingPlane: state.workingPlane,
            articleColliders: state.colliders['article'],
            buildingColliders: state.colliders['building'],
            poi: state.pois[selectedPoiIndex],
            articles: state.articles,
            instanceToGroupMap: state.instanceToGroupMap,
            articleGroups: state.articleGroups,
            settings: state.dimensionSettings
        }))
    );

    const includeAccessories = settings.showConnectors;
    const includeAnchors = settings.showAnchors;

    const accessoryIdsKey = useMemo(
        () => getAccessoryArticleIds(poi?.articles ?? {}, articles, includeAccessories, includeAnchors, elementTypes).join('|'),
        [poi?.articles, articles, includeAccessories, includeAnchors, elementTypes]
    );
    const accessoryIds = useMemo(() => (accessoryIdsKey ? accessoryIdsKey.split('|') : []), [accessoryIdsKey]);

    const fetchRepresentation = useGetMainRepresentationByArticleIdHook();
    const representationList = useQueries({
        queries: accessoryIds.map((articleId) => ({
            queryKey: ['representation', articleId, LayerLoadMode.All],
            queryFn: ({ signal }: { signal: AbortSignal }) =>
                fetchRepresentation(
                    articleId,
                    { layerLoadMode: LayerLoadMode.All, includePrefabComponents: false },
                    signal
                ),
            staleTime: REPRESENTATION_STALE_TIME,
            enabled: settings.visible
        })),
        combine: combineRepresentations
    });

    const representationsKey = representationList.map((r) => r?.id ?? '').join('|');
    const representations = useMemo(() => {
        const map = new Map<string, PrefabModel>();
        accessoryIds.forEach((articleId, index) => {
            const representation = representationList[index];
            if (representation) {
                map.set(articleId, representation);
            }
        });
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessoryIds, representationsKey]);

    const groupColors = useMemo(
        () =>
            Object.fromEntries(
                Object.entries(articleGroups)
                    .filter(([, group]) => group.type === GroupType.CUSTOM && group.color)
                    .map(([id, group]) => [id, group.color])
            ),
        [articleGroups]
    );

    const [result, setResult] = useState<{ poi: typeof poi.poi; value: DimensionDrawingResult } | null>(null);
    const completedPoi = useRef<typeof poi.poi>(undefined);
    const pendingUpdate = useRef<ReturnType<typeof setTimeout> | null>(null);
    const rebuild = useRef<(() => void) | null>(null);
    const isActive = settings.visible && !!workingPlane && !!poi;

    useEffect(() => () => {
        if (pendingUpdate.current !== null) {
            clearTimeout(pendingUpdate.current);
            pendingUpdate.current = null;
        }
    }, []);

    useEffect(() => {
        if (!settings.visible || !workingPlane || !poi) {
            if (pendingUpdate.current !== null) {
                clearTimeout(pendingUpdate.current);
                pendingUpdate.current = null;
            }
            completedPoi.current = undefined;
            return;
        }

        rebuild.current = () => {
            const state = usePlacementStore.getState();
            // Auto-insertion publishes committed copies while the selected placement indicators keep moving.
            if (completedPoi.current === poi.poi && state.isDraggingActive() && !state.isAutoDragActiveRef.current) {
                return;
            }

            const frame = createViewFrame(workingPlane.normal, mid);
            const scene = buildDimensionScene({
                elementTypes,
                frame,
                poiGuid: poi.poi.poiGuid,
                poiArticles: poi.articles ?? {},
                articles,
                representations,
                articleColliders: articleColliders ?? [],
                buildingPoints: (buildingColliders ?? [])
                    .filter((c) => c.poiIndex === selectedPoiIndex)
                    .map((c) => c.position),
                instanceToGroupMap,
                isExcluded: (instanceId) => isPlacementIndicator(instanceId, state),
                includeAccessories,
                includeAnchors
            });

            const mapping = createDisplayMapping(settings.displayMode, scene.floorY, scene.frontDepth, settings.projectionVerticalScale);
            const drawing = layoutDimensionDrawing(scene, mapping, {
                settings,
                groupColors,
                measureText
            });

            completedPoi.current = poi.poi;
            setResult({ poi: poi.poi, value: { drawing, frame, mapping } });
        };

        // Keep the pending deadline while inputs change, but build from the latest snapshot.
        if (completedPoi.current !== poi.poi && pendingUpdate.current !== null) {
            clearTimeout(pendingUpdate.current);
            pendingUpdate.current = null;
        }
        if (pendingUpdate.current === null) {
            pendingUpdate.current = setTimeout(() => {
                pendingUpdate.current = null;
                rebuild.current?.();
            }, completedPoi.current === poi.poi ? DIMENSION_UPDATE_INTERVAL_MS : 0);
        }
    }, [
        settings,
        elementTypes,
        includeAccessories,
        includeAnchors,
        workingPlane,
        poi,
        mid,
        articles,
        representations,
        articleColliders,
        buildingColliders,
        selectedPoiIndex,
        instanceToGroupMap,
        groupColors
    ]);

    return isActive && result?.poi === poi.poi ? result.value : null;
};
